/**
 * Shared API Helper Functions
 *
 * Common utilities for API service calls including authentication,
 * timeout handling, and error response processing.
 *
 * @module services/apiHelpers
 */

import { getIdentityJwt } from '@/services/identity-jwt-store';
import { authenticatedFetch as authPackageFetch } from '@hello-world-dao/auth';

// =============================================================================
// Debug Logging
// =============================================================================

const IS_DEV = import.meta.env.DEV;

/**
 * Debug logger that only logs in development mode
 * Use this instead of console.log in services
 */
export const debug = {
  log: (...args: unknown[]) => {
    if (IS_DEV) console.log(...args);
  },
  warn: (...args: unknown[]) => {
    if (IS_DEV) console.warn(...args);
  },
  error: (...args: unknown[]) => {
    // Always log errors
    console.error(...args);
  },
};

/**
 * Base error interface for API errors
 */
export interface ApiError {
  type: string;
  message: string;
}

/**
 * Get API base URL from environment
 * Defaults to oracle-bridge localhost in development
 * @throws Warning in production if VITE_API_BASE_URL is not set
 */
export function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL;
  if (!url && import.meta.env.PROD) {
    console.warn('[apiHelpers] VITE_API_BASE_URL not set in production');
  }
  // Default to oracle-bridge URL in development to avoid malformed URLs
  return url || 'http://localhost:3000';
}

/**
 * Get oracle bridge URL from environment
 * Used for WebSocket connections
 */
export function getOracleBridgeUrl(): string {
  const url = import.meta.env.VITE_ORACLE_BRIDGE_URL;
  if (!url && import.meta.env.PROD) {
    console.warn('[apiHelpers] VITE_ORACLE_BRIDGE_URL not set in production');
  }
  return url || 'http://localhost:3000';
}

/**
 * Get authorization header.
 *
 * BL-061: cookie-based auth replaced the old localStorage Bearer token.
 *
 * cross-suite-session-2026-06-18: governance now ALSO presents the
 * identity-service ES256 access JWT (rehydrated on load from the shared
 * httpOnly hwdao_refresh cookie — see services/identity-code-bootstrap). When a
 * JWT is in the in-memory store we add `Authorization: Bearer <jwt>`;
 * oracle-bridge `requireSessionAuth` verifies it against the identity-service
 * JWKS and resolves the member. When absent the request rides the session
 * cookie exactly as before (credentials:'include' is set by authenticatedFetch),
 * so this is additive and backward-compatible. The member session is now
 * JWT-based cross-suite, so without the Bearer governance data calls 401 the way
 * the dao-suite membership page did.
 */
export function getAuthHeader(): HeadersInit {
  const token = getIdentityJwt();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Bearer-attaching wrapper over `@hello-world-dao/auth`'s cookie-only
 * `authenticatedFetch` (authz-001). The auth-package fetch sends only the session
 * cookie — but oracle-bridge's `requireSessionAuth` endpoints (e.g.
 * `proposals/:id/summary`) now require the identity-service JWT bearer, which the
 * cookie flow never sets. So the suite's data calls were going cookie-only and
 * 401ing → SPA reads "session expired" → /login bounce loop. This injects
 * `Authorization: Bearer <jwt>` (from the in-memory identity-jwt-store via
 * getAuthHeader) while preserving the auth-package fetch's `Response` shape +
 * retry/session-expiry behavior. Backward-compatible: with no JWT stored,
 * getAuthHeader returns `{}` and the call is cookie-only exactly as before.
 */
export async function authenticatedFetchWithBearer(
  url: string,
  options?: RequestInit,
  config?: Parameters<typeof authPackageFetch>[2],
): Promise<Response> {
  const bearer = getAuthHeader();
  // Only rewrite options when there's a bearer to inject (or caller headers to
  // preserve) — otherwise pass options through untouched so the call shape stays
  // identical to the bare auth-package fetch (cookie-only path).
  const mergedOptions =
    Object.keys(bearer).length > 0 || options?.headers
      ? { ...options, headers: { ...(options?.headers ?? {}), ...bearer } }
      : options;
  // Avoid passing an explicit `undefined` 3rd arg (keeps the call 2-arity when no
  // config is supplied, matching the original bare-fetch call shape).
  return config === undefined
    ? authPackageFetch(url, mergedOptions)
    : authPackageFetch(url, mergedOptions, config);
}

/**
 * Create a timeout promise that rejects after specified milliseconds
 *
 * @param ms - Timeout in milliseconds
 * @param errorType - Error type to use in rejection
 * @returns Promise that rejects with timeout error
 */
export function createTimeout<T extends ApiError>(
  ms: number,
  errorType: T['type']
): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject({
        type: errorType,
        message: `Request timed out after ${ms}ms`,
      });
    }, ms);
  });
}

/**
 * Default timeout in milliseconds for API requests
 */
export const DEFAULT_TIMEOUT_MS = 30000;

/**
 * Configuration options for authenticated fetch
 */
export interface AuthenticatedFetchOptions extends Omit<RequestInit, 'signal'> {
  /** Request timeout in milliseconds. Defaults to DEFAULT_TIMEOUT_MS */
  timeout?: number;
  /** Skip authentication header (for public endpoints). Defaults to false */
  skipAuth?: boolean;
  /** Base URL override. Defaults to getApiBaseUrl() */
  baseUrl?: string;
}

/**
 * Response wrapper with typed body and error handling
 */
export interface AuthenticatedResponse<T> {
  ok: boolean;
  status: number;
  statusText: string;
  data: T | null;
  error?: ApiError;
}

/**
 * Authenticated fetch factory - automatically includes cookie-based auth credentials
 *
 * AI-037: This factory prevents the common bug of forgetting auth headers.
 * Use this for ALL authenticated API calls instead of raw fetch().
 *
 * BL-061: Updated to use cookie-based auth. credentials:'include' is always
 * set so the browser sends httpOnly session cookies. getAuthHeader() returns
 * {} so no Bearer token is sent.
 *
 * Features:
 * - Cookie-based auth via credentials:'include' (httpOnly cookies)
 * - Configurable timeout with AbortController
 * - JSON body serialization
 * - Consistent error handling
 *
 * @example
 * ```ts
 * // Simple GET request
 * const response = await authenticatedFetch<User>('/api/users/me');
 * if (response.ok) {
 *   console.log(response.data);
 * }
 *
 * // POST with JSON body
 * const response = await authenticatedFetch<CreateResult>('/api/items', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'New Item' }),
 * });
 *
 * // With custom timeout
 * const response = await authenticatedFetch<Data>('/api/slow-endpoint', {
 *   timeout: 60000, // 60 seconds
 * });
 * ```
 */
export async function authenticatedFetch<T = unknown>(
  endpoint: string,
  options: AuthenticatedFetchOptions = {}
): Promise<AuthenticatedResponse<T>> {
  const {
    timeout = DEFAULT_TIMEOUT_MS,
    skipAuth = false,
    baseUrl = getApiBaseUrl(),
    headers: customHeaders,
    ...fetchOptions
  } = options;

  // Build URL
  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;

  // Build headers with automatic auth
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(skipAuth ? {} : getAuthHeader()),
    ...customHeaders,
  };

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle non-OK responses
    if (!response.ok) {
      let errorData: ApiError;
      try {
        const errorBody = await response.json();
        errorData = {
          type: errorBody.type || 'api_error',
          message: errorBody.message || errorBody.error || response.statusText,
        };
      } catch {
        errorData = {
          type: 'api_error',
          message: response.statusText || `HTTP ${response.status}`,
        };
      }

      return {
        ok: false,
        status: response.status,
        statusText: response.statusText,
        data: null,
        error: errorData,
      };
    }

    // Parse successful response
    let data: T | null = null;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    }

    return {
      ok: true,
      status: response.status,
      statusText: response.statusText,
      data,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort/timeout
    if (error instanceof DOMException && error.name === 'AbortError') {
      return {
        ok: false,
        status: 0,
        statusText: 'Request Timeout',
        data: null,
        error: {
          type: 'timeout',
          message: `Request timed out after ${timeout}ms`,
        },
      };
    }

    // Handle network errors
    return {
      ok: false,
      status: 0,
      statusText: 'Network Error',
      data: null,
      error: {
        type: 'network_error',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    };
  }
}

/**
 * Authenticated fetch for streaming responses (SSE)
 *
 * Returns the raw Response for manual stream processing.
 * BL-061: Uses credentials:'include' for cookie-based auth (no Bearer token).
 *
 * @example
 * ```ts
 * const response = await authenticatedStreamFetch('/api/stream');
 * const reader = response.body?.getReader();
 * // Process stream...
 * ```
 */
export async function authenticatedStreamFetch(
  endpoint: string,
  options: Omit<AuthenticatedFetchOptions, 'timeout'> = {}
): Promise<Response> {
  const {
    skipAuth = false,
    baseUrl = getApiBaseUrl(),
    headers: customHeaders,
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;

  const headers: HeadersInit = {
    ...(skipAuth ? {} : getAuthHeader()),
    ...customHeaders,
  };

  return fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });
}


// =============================================================================
// Server Error-Message Selection (bl-1153 / bl-1153-1)
// =============================================================================
//
// SINGLE source of message-selection precedence for every service in this
// directory. bl-1153 fixed the vote path in isolation; bl-1153-1 converged the
// five sibling mappers here rather than letting a sixth hand-rolled
// `data.message || data.error` appear next to them.
//
// Hand-rolling the precedence is WRONG for this fleet because the servers do not
// agree with each other: oracle-bridge `/api/discussions/*` puts lower_snake
// machine codes in `error`; `/api/discussions/:id/comments*` puts bare HTTP
// reason phrases there; `middleware/session-auth.ts` sends `{error:'Unauthorized'}`
// with no `message` at all; `auth/active-membership.ts` puts the machine code in a
// THIRD field, `code`. This helper is correct across all of them precisely because
// it SUPPRESSES machine codes and bare reason phrases rather than trusting a field.

/**
 * Per-field validation detail emitted by oracle-bridge's request-validation
 * middleware (`oracle-bridge/src/middleware/validation.ts`).
 */
interface ServerErrorDetail {
  field?: unknown;
  message?: unknown;
}

/**
 * Matches a bare machine code — a single whitespace-free token of letters, digits
 * and underscores (`NOT_ELIGIBLE`, `membership_required`, `missing_signature`,
 * `FAILED`).
 *
 * oracle-bridge puts a machine code in `error` and the human sentence in `message`
 * for every MAPPED error, so surfacing `error` unconditionally renders the member a
 * bare token. Real prose contains spaces or punctuation and does not match, so it
 * still reaches the member.
 *
 * Deliberately case-INSENSITIVE in shape: the fleet emits SCREAMING_SNAKE
 * (`NOT_ELIGIBLE`, `MEMBERSHIP_REQUIRED`) *and* lower_snake (`missing_signature`,
 * `invalid_timestamp`, `membership_required` — see oracle-bridge `hmac-auth.ts` and
 * `auth/active-membership.ts`). A SCREAMING_SNAKE-only rule leaked the lower_snake
 * half straight to the member.
 */
const MACHINE_CODE_PATTERN = /^[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)*$/;

/**
 * Bare HTTP reason phrases, lowercased.
 *
 * Several oracle-bridge middlewares answer with only the reason phrase in `error`
 * and no `message` — `{error:'Unauthorized'}` from `session-auth.ts`, `{error:'Forbidden'}`
 * from `admin-auth.ts`. That text carries nothing the status code did not already
 * carry, and it is strictly worse member-facing copy than the curated per-status
 * string it would replace, so it is suppressed and the canned copy wins.
 */
const HTTP_REASON_PHRASES = new Set([
  'bad request',
  'unauthorized',
  'payment required',
  'forbidden',
  'not found',
  'method not allowed',
  'not acceptable',
  'request timeout',
  'conflict',
  'gone',
  'payload too large',
  'unsupported media type',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'not implemented',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
]);

/** Upper bound on composed `details[]` output — the server controls its length. */
const MAX_DETAIL_ENTRIES = 5;

/** Non-empty trimmed string, or undefined. */
function proseOrUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Non-empty trimmed string carrying real human information, or undefined.
 *
 * Rejects bare machine codes and bare HTTP reason phrases — both are tokens the
 * member cannot act on, and suppressing them lets the curated per-status string win.
 */
function humanProseOrUndefined(value: unknown): string | undefined {
  const prose = proseOrUndefined(value);
  if (prose === undefined) return undefined;
  if (MACHINE_CODE_PATTERN.test(prose)) return undefined;
  if (HTTP_REASON_PHRASES.has(prose.toLowerCase())) return undefined;
  return prose;
}

/**
 * Render a `details[]` array as `field — message; field — message`.
 *
 * Capped at {@link MAX_DETAIL_ENTRIES}: the array length is server-controlled and the
 * result is rendered into a single paragraph in `VoteConfirmationModal`, so an
 * unbounded join can overwhelm the dialog. Suppressed entries are counted, not hidden.
 */
function formatServerDetails(details: unknown): string | undefined {
  if (!Array.isArray(details)) return undefined;

  const parts = details
    .map((entry): string | undefined => {
      if (typeof entry !== 'object' || entry === null) return undefined;
      const { field, message } = entry as ServerErrorDetail;
      const text = proseOrUndefined(message);
      if (text === undefined) return undefined;
      const name = proseOrUndefined(field);
      return name === undefined ? text : `${name} — ${text}`;
    })
    .filter((part): part is string => part !== undefined);

  if (parts.length === 0) return undefined;

  const shown = parts.slice(0, MAX_DETAIL_ENTRIES);
  const hidden = parts.length - shown.length;
  return hidden > 0 ? `${shown.join('; ')} (+${hidden} more)` : shown.join('; ');
}

/**
 * Pick the member-visible sentence out of a parsed API error body.
 *
 * Precedence (bl-1153, converging on the `apiHelpers.ts` reference implementation):
 * `message` → `details[]` → `error` (prose only). When a base sentence AND per-field
 * details are both present they are composed, so `details[]` is never dropped.
 *
 * Returns `undefined` when the body carries no human text — callers then keep their
 * existing canned per-status string.
 *
 * NOTE: this deliberately surfaces the server's text VERBATIM. Curated member-facing
 * copy for raw canister strings is a separate, Menley-owned follow-up; surfacing the
 * real message is strictly better than naming the wrong subsystem.
 *
 * @param data - Parsed response body (never a `Response` — see `readErrorBody`)
 */
export function selectServerMessage(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;

  const body = data as Record<string, unknown>;
  const base = proseOrUndefined(body.message) ?? humanProseOrUndefined(body.error);
  const detailText = formatServerDetails(body.details);

  if (base !== undefined && detailText !== undefined) return `${base}: ${detailText}`;
  return base ?? detailText;
}

/**
 * Read an optional string field off an already-parsed error body.
 *
 * Several mappers carry a server-supplied correlation id alongside the message
 * (`existingDiscussionId` on a 409 from `/api/discussions/from-capture`,
 * `existingThreadId` on a 409 from the continuation route). They used to pull it
 * out with their OWN `await response.json()`, which is the second read this story
 * removes; they now take it from the single parsed body via this guard.
 *
 * Returns `undefined` for a missing key or a non-string value.
 */
export function readStringField(data: unknown, key: string): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Read an error response body exactly once.
 *
 * A `Response` body stream can only be read once. Every error path in `src/services/`
 * funnels through here so that no `Response` is ever `.json()`-ed twice — the
 * bl-1153 defect was a second read silently rejecting and stranding a hardcoded
 * fallback string.
 *
 * Fails closed: an unparseable body (HTML gateway page, empty, truncated) yields
 * `undefined` rather than throwing, so callers still emit a `GovernanceError`.
 */
export async function readErrorBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    // Not JSON (HTML error page, empty body, truncated stream) — fail closed.
    return undefined;
  }
}
